/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';

interface InvestorInquiry {
  _id: string;
  name: string;
  email: string;
  organization?: string;
  jurisdiction?: string;
  ticketSizeUsd?: number | null;
  dealInterest?: string;
  timing?: string;
  accredited?: boolean;
  message?: string;
  submittedAt: string;
}

const TIMING_LABELS: Record<string, string> = {
  now: 'Ready now',
  'this-quarter': 'This quarter',
  'this-year': 'This year',
  exploring: 'Exploring',
};

const JURISDICTION_LABELS: Record<string, string> = {
  US: 'United States',
  UK: 'United Kingdom',
  EU: 'EU',
  MEA: 'Middle East & Africa',
  APAC: 'Asia Pacific',
  OTHER: 'Other',
};

const PANEL_STYLE: React.CSSProperties = {
  backgroundColor: '#0B222C',
  border: '2px solid #095D52',
  boxShadow:
    '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
  backgroundImage:
    'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
};

const formatTicket = (value?: number | null) =>
  typeof value === 'number' ? `$${value.toLocaleString('en-US')}` : '—';

const formatDate = (value: string) =>
  value ? new Date(value).toLocaleString('en-GB') : '—';

/** Investor applications submitted through the /raise verification form. */
const InvestorApplicationsPanel: React.FC = () => {
  const [inquiries, setInquiries] = useState<InvestorInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<InvestorInquiry | null>(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await services.get(
          `${API_ENDPOINTS.INVESTOR_INQUIRIES.LIST}?page=${currentPage}&limit=${itemsPerPage}`
        );

        if (response.data && response.data.success) {
          const data = response.data.data || [];
          setInquiries(Array.isArray(data) ? data : []);
          setTotal(response.data.total || data.length);
          setTotalPages(response.data.pages || 1);
        } else {
          setInquiries([]);
          setTotal(0);
          setTotalPages(1);
        }
      } catch (err: any) {
        console.error('Error fetching investor applications:', err);
        setError(err.response?.data?.message || 'Failed to fetch investor applications');
        setInquiries([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchInquiries();
  }, [currentPage]);

  const search = searchTerm.trim().toLowerCase();
  const visible = search
    ? inquiries.filter((i) =>
        [i.name, i.email, i.organization, i.dealInterest]
          .filter(Boolean)
          .some((field) => (field as string).toLowerCase().includes(search))
      )
    : inquiries;

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-white text-lg'>Loading investor applications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-red-400 text-lg'>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className='flex flex-col md:flex-row md:items-center md:justify-between mb-8'>
        <div>
          <h1
            className='text-3xl font-bold mb-2 font-dm-serif'
            style={{
              color: '#ede8c5',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
              letterSpacing: '0.5px',
            }}
          >
            Investor
          </h1>
          <p className='font-work-sans' style={{ color: '#ede8c5' }}>
            Verification requests submitted through the investor access form
          </p>
        </div>
        <div className='font-work-sans text-sm' style={{ color: '#ede8c5' }}>
          {total} application{total === 1 ? '' : 's'}
        </div>
      </div>

      {/* Search */}
      <div className='mb-8'>
        <div className='rounded-lg p-6 shadow-lg transition-all duration-300' style={PANEL_STYLE}>
          <label
            htmlFor='investor-search'
            className='block text-sm font-medium mb-2 font-work-sans'
            style={{ color: '#ede8c5' }}
          >
            Search Applications
          </label>
          <input
            type='text'
            id='investor-search'
            placeholder='Search by name, email, firm, or deal interest...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:border-transparent font-work-sans'
            style={{
              backgroundColor: '#0B222C',
              border: '1px solid #095D52',
              color: '#ede8c5',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className='rounded-lg border shadow-lg transition-all duration-300' style={PANEL_STYLE}>
        {visible.length === 0 ? (
          <div className='p-12 text-center font-work-sans' style={{ color: '#ede8c5' }}>
            <p className='text-lg mb-1'>No investor applications found</p>
            <p className='text-sm opacity-75'>
              Applications will appear here once submitted through the access form
            </p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr style={{ borderBottom: '1px solid #095D52' }}>
                  {['Investor', 'Firm', 'Jurisdiction', 'Ticket', 'Timing', 'Accredited', 'Submitted', ''].map(
                    (heading) => (
                      <th
                        key={heading}
                        className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider font-work-sans'
                        style={{ color: '#bbe425' }}
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {visible.map((inquiry) => (
                  <tr key={inquiry._id} style={{ borderBottom: '1px solid rgba(9, 93, 82, 0.5)' }}>
                    <td className='px-6 py-4'>
                      <div
                        className='text-sm font-semibold font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        {inquiry.name}
                      </div>
                      <div
                        className='text-xs font-work-sans opacity-75'
                        style={{ color: '#ede8c5' }}
                      >
                        {inquiry.email}
                      </div>
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {inquiry.organization || '—'}
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {JURISDICTION_LABELS[inquiry.jurisdiction || ''] || '—'}
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {formatTicket(inquiry.ticketSizeUsd)}
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {TIMING_LABELS[inquiry.timing || ''] || '—'}
                    </td>
                    <td className='px-6 py-4'>
                      <span
                        className='inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium font-work-sans'
                        style={{
                          backgroundColor: inquiry.accredited ? '#10B981' : '#EF4444',
                          color: '#ffffff',
                        }}
                      >
                        {inquiry.accredited ? 'Self-certified' : 'Not certified'}
                      </span>
                    </td>
                    <td className='px-6 py-4 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                      {formatDate(inquiry.submittedAt)}
                    </td>
                    <td className='px-6 py-4'>
                      <button
                        onClick={() => setSelected(inquiry)}
                        className='px-3 py-1.5 rounded-lg text-xs font-work-sans transition-all duration-200 hover:scale-105'
                        style={{
                          backgroundColor: '#095D52',
                          color: '#ede8c5',
                          border: '1px solid #095D52',
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-6'>
          <span className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
            Page {currentPage} of {totalPages}
          </span>
          <div className='flex gap-2'>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className='px-4 py-2 rounded-lg font-work-sans disabled:opacity-40'
              style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
            >
              Prev
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className='px-4 py-2 rounded-lg font-work-sans disabled:opacity-40'
              style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center p-4'
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={() => setSelected(null)}
        >
          <div
            className='rounded-lg p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto'
            style={PANEL_STYLE}
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-start justify-between mb-6'>
              <h2 className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {selected.name}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className='text-2xl leading-none'
                style={{ color: '#ede8c5' }}
                aria-label='Close'
              >
                ×
              </button>
            </div>
            <dl className='grid grid-cols-1 sm:grid-cols-2 gap-5 font-work-sans'>
              {[
                ['Email', selected.email],
                ['Firm', selected.organization || '—'],
                ['Jurisdiction', JURISDICTION_LABELS[selected.jurisdiction || ''] || '—'],
                ['Indicative ticket', formatTicket(selected.ticketSizeUsd)],
                ['Deal interest', selected.dealInterest || '—'],
                ['Timing', TIMING_LABELS[selected.timing || ''] || '—'],
                ['Accredited', selected.accredited ? 'Self-certified' : 'Not certified'],
                ['Submitted', formatDate(selected.submittedAt)],
              ].map(([label, value]) => (
                <div key={label as string}>
                  <dt className='text-xs uppercase tracking-wider mb-1' style={{ color: '#bbe425' }}>
                    {label}
                  </dt>
                  <dd className='text-sm' style={{ color: '#ede8c5' }}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
            {selected.message && (
              <div className='mt-6'>
                <dt className='text-xs uppercase tracking-wider mb-1' style={{ color: '#bbe425' }}>
                  Message
                </dt>
                <dd className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                  {selected.message}
                </dd>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorApplicationsPanel;
